import { useSyncExternalStore } from 'react';

import { currentMonth, shiftMonth } from './hq-dates';

/**
 * The month Dale HQ is looking at, shared by the top bar, Inicio and Finanzas
 * (each admin route mounts its own shell, so it lives here, not in a component).
 * Until somebody picks another it follows the current Panama month, so a panel
 * left open across midnight of the 1st moves on by itself. Going forward stops at
 * the current month; reaching it again goes back to following it.
 */

const listeners = new Set<() => void>();
/** The month somebody picked, or null while it follows the clock. */
let chosen: string | null = null;

export function getHqMonth(now: Date = new Date()): string {
  return chosen ?? currentMonth(now);
}

export function setHqMonth(month: string | null): void {
  chosen = month;
  listeners.forEach((listener) => listener());
}

/** One month back or forward, never past the current one. */
export function shiftHqMonth(delta: number, now: Date = new Date()): void {
  const next = shiftMonth(getHqMonth(now), delta);
  setHqMonth(next >= currentMonth(now) ? null : next);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useHqMonth() {
  useSyncExternalStore(subscribe, () => chosen, () => chosen);
  const now = new Date();
  const month = getHqMonth(now);
  return {
    month,
    isCurrent: month === currentMonth(now),
    shift: (delta: number) => shiftHqMonth(delta),
    reset: () => setHqMonth(null),
  };
}
